/**
 * @description 自动注入埋点代码的Vite插件，在Vue单文件组件的click事件中自动添加埋点统计代码
 * @version 1.0.0
 * @author CTC开发团队
 * @refer https://juejin.cn/post/7357609459345506330?earchId=20250730133928053080265BFB46DB6163
 */

// inject-click-handler.ts vite插件 - 用于自动注入点击事件埋点
import { parse } from 'vue/compiler-sfc';
import { Plugin } from 'vite';

// 定义替换规则接口
interface Replacement {
  source: string;        // 原始代码片段
  replaceSource: string; // 替换后的代码片段
}

/**
 * 查找节点中的click事件属性
 * @param node AST节点
 * @returns click事件属性对象，如果未找到则返回undefined
 */
const findClickEvent = (node) => node.props.find(
  (prop) =>
    prop.type === 7 &&      // 指令类型（v-on:click或@click）
    prop.name === 'on' &&   // 事件指令名称
    prop.arg?.content === 'click', // 事件类型为click
)

/**
 * 获取标签的文本内容
 * @param node AST节点
 * @returns 标签的文本内容
 */
const findTagTextContent = (node) => {
  const tagText = node.children.find((item) => item.type === 2)?.content ?? '';
  return tagText;
}

/**
 * 判断表达式是否为函数调用格式的正则表达式
 * 包含两个匹配模式（使用 | 分隔）：
 *
 * 模式1: ^\s*[a-zA-Z_$][\w$]*\s*\([^)]*\)\s*$
 * - 匹配带括号的函数调用，如 "handleClick()", "submit(event)"
 *
 * 模式2: ^\s*[a-zA-Z_$][\w$]*\s*$
 * - 匹配不带括号的函数名，如 "handleClick", "submit"
 *
 * 用于识别Vue模板中的click事件表达式类型
 * 决定是否需要为函数添加括号或保持原有格式
 */
const FUNCTION_CALL_REGEX = /^\s*[a-zA-Z_$][\w$]*\s*\([^)]*\)\s*$|^\s*[a-zA-Z_$][\w$]*\s*$/;

/**
 * 检测并修复函数调用表达式格式
 * @param expContent 表达式内容
 * @returns 修复后的表达式
 */
const fixFunctionCallExpression = (expContent: string): string => {
  const isFunctionCall = FUNCTION_CALL_REGEX.test(expContent);
  return isFunctionCall && !/\([^)]*\)/.test(expContent)
    ? `${expContent}()`
    : expContent; // 类似 isExpand = false的表达式
}

/**
 * 创建埋点事件字符串并替换节点
 * @param node AST节点
 * @param clickEvent click事件对象
 * @param eventContent 埋点事件内容
 * @param replacements 替换列表
 */
const createTrackingEventAndReplace = (
  node: any,
  clickEvent: any,
  eventContent: string,
  replacements: Replacement[]
) => {
  const expContent = clickEvent.exp?.content || '';
  const fixedExp = fixFunctionCallExpression(expContent);

  const newEventString = `@click.stop="${eventContent};${fixedExp}"`;

  // 创建新节点字符串（替换原事件）
  const newNodeStr = node.loc.source
    .replace(clickEvent.loc.source, newEventString)
    .trim();

  replacements.push({
    source: node.loc.source,
    replaceSource: newNodeStr,
  });
}



/**
 * 创建自动注入点击埋点的Vite插件
 * 该插件会在构建时自动分析Vue单文件组件，为具有click事件的元素注入埋点代码
 * @returns Vite插件对象
 */
export default (): Plugin => ({
  name: 'inject-click-handler', // 插件名称
  /**
   * 转换函数，在构建过程中处理文件
   * @param code 文件原始代码
   * @param id 文件ID（路径）
   * @returns 转换后的代码或null
   */
  transform(code, id) {
    try {
      // 只处理.vue文件
      if (!/.vue$/.test(id)) return null;

      // 解析Vue单文件组件的AST
      const parseCode = parse(code);
      if (!parseCode) return null;
      if (!parseCode.descriptor?.template?.content) return null;

      // 获取模板内容
      const { content } = parseCode.descriptor.template;

      let $code = content;                  // 修改后的模板代码
      const replacements: Replacement[] = []; // 存储所有需要替换的代码片段

      /**
       * 递归遍历AST节点，查找需要注入埋点的元素
       * @param node 当前AST节点
       */
      const traverse = (node: any) => {
        // 处理当前节点：检查是否有属性
        if (node?.props?.length) {
          // 查找data-manual-track属性（手动埋点标识）
          const mdProp = node.props.find(
            (prop) =>
              (prop.type === 6 && prop.name === 'data-manual-track') || // 静态属性
              (prop.type === 7 &&
                prop.name === 'bind' &&
                prop.arg?.content === 'data-manual-track'), // 动态绑定属性
          );

          // 如果元素有data-manual-track属性，则处理手动埋点
          if (mdProp) {
            const tagName = node.tag; // 获取标签名

            // 获取埋点内容（静态值或表达式）
            const mdContent =
              mdProp.type === 6
                ? `'${mdProp.value?.content || ''}'`  // 静态属性值
                : mdProp.type === 7
                  ? mdProp.exp?.content || ''          // 动态表达式
                  : '';

            // 查找现有的click事件
            const clickEvent = findClickEvent(node)

            let newEventString = '';

            const text = findTagTextContent(node);

            // 如果元素已有click事件，则在原有事件前面添加埋点代码
            if (clickEvent) {
              // 构造手动埋点事件内容
              const eventContent = `sendMd({
                content:${mdContent},
                tag:'${tagName}',
                text:'${text}'
              })`;

              // 创建埋点事件并替换节点
              createTrackingEventAndReplace(node, clickEvent, eventContent, replacements);
            } else {
              // 元素没有click事件时，直接添加埋点click事件
              newEventString = `@click.stop="sendMd({
                content:${mdContent},
                tag:${tagName},
                text:'${text}'
              })"`;

              // 查找标签结束位置
              const closingBracketIndex = node.loc.source.lastIndexOf('>');
              // 判断是否为自闭合标签（如<img />）
              const isSelfClosing =
                node.loc.source[closingBracketIndex - 1] === '/';

              // 在开始标签末尾添加click事件
              let newNodeStr;
              if (isSelfClosing) {
                // 自闭合标签：在/>前添加事件
                newNodeStr =
                  node.loc.source.slice(0, closingBracketIndex - 1).trim() +
                  ` ${newEventString} />`;
              } else {
                // 普通标签：在>前添加事件
                newNodeStr =
                  node.loc.source.slice(0, closingBracketIndex).trim() +
                  ` ${newEventString}>`;
              }

              // 添加到替换列表
              replacements.push({
                source: node.loc.source,
                replaceSource: newNodeStr,
              });
            }
          } else {
            const tagName = node.tag;
            if (tagName === 'navigator' && node.type === 1) {
              // 如果是navigator元素，因为uni.addInterceptor没法监听到对应的跳转，所以单独处理
              const url =
                node.props.find((item) => item.name === 'url')?.value
                  ?.content ??
                node.props.find((item) => item.rawName === ':url')?.exp
                  ?.content;

              // 创建新节点字符串（新增事件）
              // 捕获标签内的所有属性（除了 > 之外的任何字符）
              // $1 是正则表达式中第一个捕获组的内容（即原有的属性）
              const newNodeStr = node.loc.source
                .replace(
                  /<navigator([^>]*)>/,
                  `<navigator$1 @click.stop="sendPageMd('${url}',{},'${tagName}')">`,
                )
                .trim();

              replacements.push({
                source: node.loc.source,
                replaceSource: newNodeStr,
              });
            }


            // 查找元素是否有click事件
            const clickEvent = findClickEvent(node)

            // 获取元素的文本内容（用于自动埋点）
            const text =
              findTagTextContent(node);

            // 如果元素已有click事件，则添加自动埋点
            if (clickEvent) {
              // 构造自动埋点事件内容
              const eventContent = `sendMd({
                tag: '${tagName}',
                text:'${text}'
               })`;

              // 创建埋点事件并替换节点
              createTrackingEventAndReplace(node, clickEvent, eventContent, replacements);
            }
          }
        }

        // 递归遍历子节点（深度优先策略）
        if (node.children) {
          node.children.forEach((child: any) => traverse(child));
        }
      };

      // 开始遍历AST根节点
      traverse(parseCode.descriptor.template.ast);

      // 按节点长度倒序替换（避免嵌套节点替换冲突问题）
      // 先替换长的节点，再替换短的节点，确保嵌套结构正确处理
      replacements
        .sort((a, b) => b.source.length - a.source.length)
        .forEach(({ source, replaceSource }) => {
          $code = $code.replace(source, replaceSource);
        });

      // 返回修改后的代码和SourceMap
      return {
        code: code.replace(content, $code), // 替换模板内容
        map: null,                          // 不生成SourceMap
      };
    } catch (e) {
      // 捕获并记录埋点注入过程中的错误
      console.error(`埋点注入失败: ${e}`);
      return null; // 返回null，保持原始代码不变
    }
  },
});
